import EngineerThreadsAPI from '../../api/engineerThreads';
import {
  areSameThread,
  shouldCommitThreadMessagesToCurrentView,
} from '../../helper/engineerThreadMessages';

// Promises stay outside Vuex state. Each store and thread owns its request,
// so an overlapping read of another thread cannot defeat deduplication.
const messageFetchesByState = new WeakMap();

const isAbortError = (error) =>
  error?.name === 'AbortError' ||
  error?.name === 'CanceledError' ||
  error?.code === 'ERR_CANCELED';

const throwIfAborted = (signal) => {
  if (signal?.aborted) {
    const error = new Error('Engineer thread request aborted');
    error.name = 'AbortError';
    throw error;
  }
};

const commitFreeThreadState = (commit, thread, messages = []) => {
  if (!thread) {
    return null;
  }

  commit('SET_THREAD', thread);
  commit('SET_CURRENT_THREAD_ID', thread.id);
  commit('SET_MESSAGES', { messages, threadType: 'free' });
  return thread;
};

export const state = {
  records: {},
  uiFlags: {
    isFetchingThread: false,
    isFetchingMessages: false,
    isFetchingFreeThread: false,
    isFetchingOlderMessages: false,
  },
  currentThreadId: null,
  fetchingMessagesThreadId: null,
  messages: [],
  freeThread: {
    data: null,
    messages: [],
  },
  // Cache per conversationId: { [conversationId]: { data: thread, messages: [], messagesMeta: {} } }
  conversationThreadCache: {},
  // Manteniamo conversationThread come puntatore al thread corrente per retrocompatibilità getter
  conversationThread: {
    data: null,
    messages: [],
  },
};

export const getters = {
  getThreadByConversationId: (_state) => (conversationId) => {
    // Prima cerchiamo nella cache
    const cached = _state.conversationThreadCache[conversationId];
    if (cached?.data) return cached.data;
    return Object.values(_state.records).find(
      (thread) => thread.conversation_id === conversationId
    );
  },
  getCurrentThread: (_state) => _state.records[_state.currentThreadId] || null,
  getThreadMessages: (_state) => {
    if (
      _state.currentThreadId &&
      _state.records[_state.currentThreadId]?.thread_type === 'conversation'
    ) {
      return _state.conversationThread.messages;
    }
    return _state.freeThread.messages;
  },
  getFreeThread: (_state) => _state.freeThread.data,
  getFreeThreadMessages: (_state) => _state.freeThread.messages,
  getConversationThread: (_state) => _state.conversationThread.data,
  getConversationThreadMessages: (_state) => _state.conversationThread.messages,
  getUIFlags: (_state) => _state.uiFlags,
  getMessagesMeta: (_state) => {
    const currentThread = _state.records[_state.currentThreadId];
    if (!currentThread) return null;
    if (currentThread.thread_type === 'conversation') {
      const cached =
        _state.conversationThreadCache[currentThread.conversation_id];
      return cached?.messagesMeta || null;
    }
    return null;
  },
};

export const actions = {
  async fetch(
    { commit, state: threadState, dispatch },
    { conversationId, signal }
  ) {
    if (!conversationId) {
      return null;
    }

    // Forziamo il reset del flag se è rimasto bloccato
    if (threadState.uiFlags.isFetchingThread) {
      commit('SET_UI_FLAG', { isFetchingThread: false });
    }

    // Controlliamo la cache prima di fare la request
    const cached = threadState.conversationThreadCache[conversationId];
    if (cached?.data) {
      throwIfAborted(signal);
      commit('SET_THREAD', cached.data);
      commit('SET_CURRENT_THREAD_ID', cached.data.id);
      // Ripristiniamo i messaggi dalla cache
      commit('SET_MESSAGES', {
        messages: cached.messages || [],
        threadType: 'conversation',
      });
      return cached.data;
    }

    // Verifichiamo se stiamo già gestendo questo thread
    if (threadState.currentThreadId) {
      const currentThread = threadState.records[threadState.currentThreadId];
      if (currentThread?.conversation_id === conversationId) {
        throwIfAborted(signal);
        return currentThread;
      }
    }

    commit('SET_UI_FLAG', { isFetchingThread: true });

    try {
      // Resettiamo solo il puntatore corrente, non la cache
      commit('SET_CURRENT_THREAD_ID', null);

      const response = await EngineerThreadsAPI.getByConversationId(
        conversationId,
        { includeMessages: true, signal }
      );
      throwIfAborted(signal);

      if (!response) {
        throw new Error('Risposta API vuota');
      }

      const thread = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      if (thread) {
        // Estraiamo i messaggi dalla risposta unificata
        const messages = thread.thread_messages || [];
        const messagesMeta = thread.messages_meta || null;

        // Rimuoviamo thread_messages/messages_meta dal thread data
        const threadData = { ...thread };
        delete threadData.thread_messages;
        delete threadData.messages_meta;

        commit('SET_THREAD', threadData);
        commit('SET_CURRENT_THREAD_ID', threadData.id);
        commit('SET_MESSAGES', { messages, threadType: 'conversation' });
        commit('SET_CONVERSATION_THREAD_CACHE', {
          conversationId,
          data: threadData,
          messages,
          messagesMeta,
        });
        return threadData;
      }

      return null;
    } catch (error) {
      if (isAbortError(error)) {
        return null;
      }
      if (error.response?.status === 404) {
        try {
          const newThread = await dispatch('createConversationThread', {
            conversationId,
          });
          return newThread;
        } catch {
          return null;
        }
      }
      return null;
    } finally {
      commit('SET_UI_FLAG', { isFetchingThread: false });
    }
  },

  async fetchMessages(
    { commit, state: threadState },
    { threadId, signal = null, refresh = false }
  ) {
    if (!threadId || signal?.aborted) return [];

    let requests = messageFetchesByState.get(threadState);
    if (!requests) {
      requests = new Map();
      messageFetchesByState.set(threadState, requests);
    }
    const key = String(threadId);
    const pending = requests.get(key);
    if (pending) {
      if (!refresh) return [];
      // A completion event may follow the snapshot of the current HTTP read.
      // Coalesce completions into one later read, with the latest load signal.
      pending.nextRequest = { signal };
      return pending.promise;
    }

    const request = { nextRequest: null, promise: null };
    requests.set(key, request);
    commit('SET_FETCHING_MESSAGES_THREAD_ID', threadId);
    commit('SET_UI_FLAG', { isFetchingMessages: true });

    request.promise = (async () => {
      let options = { signal };
      let messages = [];
      try {
        do {
          request.nextRequest = null;
          try {
            throwIfAborted(options.signal);
            // Reads must run in sequence so a completion gets a newer snapshot.
            // eslint-disable-next-line no-await-in-loop
            const response = await EngineerThreadsAPI.getMessages(
              threadId,
              options
            );
            throwIfAborted(options.signal);
            const data = response.data;
            // Both paginated responses and legacy arrays are supported.
            messages = data.messages || data;
            const meta = data.meta || null;
            const thread = threadState.records[threadId];
            const threadType = thread?.thread_type || 'free';
            if (
              shouldCommitThreadMessagesToCurrentView({
                currentThreadId: threadState.currentThreadId,
                responseThreadId: threadId,
              })
            ) {
              commit('SET_MESSAGES', { messages, threadType });
            }
            if (threadType === 'conversation' && thread?.conversation_id) {
              commit('SET_CONVERSATION_THREAD_CACHE', {
                conversationId: thread.conversation_id,
                data: thread,
                messages,
                messagesMeta: meta,
              });
            }
          } catch (_) {
            // A cancelled/failed older read must not discard a queued refresh.
            messages = [];
          }
          options = request.nextRequest;
        } while (options);
        return messages;
      } finally {
        requests.delete(key);
        if (areSameThread(threadState.fetchingMessagesThreadId, threadId)) {
          commit('SET_FETCHING_MESSAGES_THREAD_ID', null);
          commit('SET_UI_FLAG', { isFetchingMessages: false });
        }
      }
    })();
    return request.promise;
  },

  async fetchOlderMessages({ commit, state: threadState }, { threadId }) {
    const emptyResult = { messages: [], meta: null };
    if (threadState.uiFlags.isFetchingOlderMessages) {
      return emptyResult;
    }

    if (!threadId) return emptyResult;

    const thread = threadState.records[threadId];
    const threadType = thread?.thread_type || 'free';

    // Troviamo il messaggio più vecchio per before_id
    let currentMessages;
    if (threadType === 'conversation') {
      currentMessages = threadState.conversationThread.messages;
    } else {
      currentMessages = threadState.freeThread.messages;
    }

    if (!currentMessages || currentMessages.length === 0) return emptyResult;

    const oldestMessage = currentMessages[0];
    if (!oldestMessage?.id) return emptyResult;

    commit('SET_UI_FLAG', { isFetchingOlderMessages: true });

    try {
      const response = await EngineerThreadsAPI.getOlderMessages(threadId, {
        beforeId: oldestMessage.id,
      });

      const data = response.data;
      const olderMessages = data.messages || data;
      const meta = data.meta || null;
      const shouldCommitToCurrentView = shouldCommitThreadMessagesToCurrentView(
        {
          currentThreadId: threadState.currentThreadId,
          responseThreadId: threadId,
        }
      );

      if (olderMessages.length > 0) {
        if (shouldCommitToCurrentView) {
          commit('PREPEND_MESSAGES', { messages: olderMessages, threadType });
        }

        // Aggiorniamo la cache se è un thread di conversazione
        if (threadType === 'conversation' && thread?.conversation_id) {
          const cachedMessages =
            threadState.conversationThreadCache[thread.conversation_id]
              ?.messages || [];
          const messagesForCache = shouldCommitToCurrentView
            ? threadState.conversationThread.messages || []
            : cachedMessages;
          const allMessages = [...olderMessages, ...messagesForCache];
          commit('SET_CONVERSATION_THREAD_CACHE', {
            conversationId: thread.conversation_id,
            data: thread,
            messages: allMessages,
            messagesMeta: meta,
          });
        }
      }

      return { messages: olderMessages, meta };
    } catch (error) {
      return { messages: [], meta: null };
    } finally {
      commit('SET_UI_FLAG', { isFetchingOlderMessages: false });
    }
  },

  async createOrGetFreeThread(
    { commit, state: threadState },
    { signal = null } = {}
  ) {
    if (threadState.uiFlags.isFetchingFreeThread && !signal) {
      return null;
    }

    throwIfAborted(signal);
    commit('SET_UI_FLAG', { isFetchingFreeThread: true });

    let thread = null;
    let messages = [];

    try {
      const response = await EngineerThreadsAPI.getFreeThread({
        includeMessages: true,
        signal,
      });
      throwIfAborted(signal);
      if (response.data) {
        const data = response.data;
        messages = data.thread_messages || [];
        // Rimuoviamo thread_messages/messages_meta dal thread data
        thread = { ...data };
        delete thread.thread_messages;
        delete thread.messages_meta;
      }
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }
      if (error.response?.status === 404) {
        try {
          const newThread = await EngineerThreadsAPI.createFreeThread({
            signal,
          });
          throwIfAborted(signal);
          thread = newThread.data;
          messages = [];
        } catch (createError) {
          if (isAbortError(createError)) {
            throw createError;
          }
          throw createError;
        }
      } else {
        throw error;
      }
    } finally {
      commit('SET_UI_FLAG', { isFetchingFreeThread: false });
    }

    if (thread) {
      throwIfAborted(signal);
      return commitFreeThreadState(commit, thread, messages);
    }

    return null;
  },

  async createConversationThread({ commit }, { conversationId }) {
    if (!conversationId) {
      throw new Error(
        'conversationId è richiesto per creare un thread di conversazione'
      );
    }

    try {
      const response =
        await EngineerThreadsAPI.createConversationThread(conversationId);

      if (!response || !response.data) {
        throw new Error(
          '❌ [engineerThreads/createConversationThread] Risposta API vuota o non valida'
        );
      }

      const thread = response.data;

      commit('SET_THREAD', thread);
      commit('SET_CURRENT_THREAD_ID', thread.id);
      commit('SET_MESSAGES', { messages: [], threadType: 'conversation' });
      commit('SET_CONVERSATION_THREAD_CACHE', {
        conversationId,
        data: thread,
        messages: [],
        messagesMeta: null,
      });
      return thread;
    } catch (error) {
      throw new Error('Impossibile creare il thread di conversazione');
    }
  },

  async createMessage(
    { commit, state: threadState },
    {
      threadId,
      content,
      role = 'user',
      conversationId,
      metadata = null,
      skipLocalAdd = false,
    }
  ) {
    if (conversationId && !threadId) {
      const newThread =
        await EngineerThreadsAPI.createConversationThread(conversationId);
      threadId = newThread.data.id;
      commit('SET_THREAD', newThread.data);
      commit('SET_CURRENT_THREAD_ID', newThread.data.id);
    }

    const threadMessage = { role, content };
    if (metadata) {
      threadMessage.metadata = metadata;
    }
    const response = await EngineerThreadsAPI.createMessage(threadId, {
      thread_message: threadMessage,
    });

    const message = response.data;

    if (!skipLocalAdd) {
      const thread = threadState.records[threadId];
      const threadType = thread?.thread_type || 'free';

      commit('ADD_MESSAGE', { message, threadType });
    }
    return message;
  },

  clearCurrentThread({ commit }) {
    // Resettiamo solo il puntatore, NON la cache
    commit('SET_CURRENT_THREAD_ID', null);
    commit('CLEAR_FREE_THREAD');
    // Puliamo solo il puntatore conversationThread corrente, non la cache
    commit('CLEAR_CONVERSATION_THREAD');
  },

  async archiveThread({ commit, state: threadState }, expected = {}) {
    const threadId = expected.threadId || threadState.currentThreadId;

    if (!threadId) {
      return false;
    }

    const thread = threadState.records[threadId];
    if (!thread) {
      return false;
    }

    const threadType = thread?.thread_type;
    const conversationId = thread?.conversation_id;
    const hasExpectedConversationId = Object.prototype.hasOwnProperty.call(
      expected,
      'conversationId'
    );

    if (expected.threadType && threadType !== expected.threadType) {
      return false;
    }

    if (
      hasExpectedConversationId &&
      Number(conversationId || 0) !== Number(expected.conversationId || 0)
    ) {
      return false;
    }

    await EngineerThreadsAPI.update(threadId, {
      is_active: false,
      is_deleted: true,
    });

    if (threadState.currentThreadId === threadId) {
      commit('SET_CURRENT_THREAD_ID', null);
    }

    if (threadType === 'conversation') {
      commit('CLEAR_CONVERSATION_THREAD');
    } else if (threadType === 'free') {
      commit('CLEAR_FREE_THREAD');
    }

    if (threadType === 'conversation' && conversationId) {
      commit('CLEAR_CONVERSATION_THREAD_CACHE_ENTRY', conversationId);
    }

    if (threadType === 'free') {
      commit('SET_UI_FLAG', { isFetchingFreeThread: true });
      try {
        const newThread = await EngineerThreadsAPI.createFreeThread();
        commitFreeThreadState(commit, newThread?.data, []);
      } finally {
        commit('SET_UI_FLAG', { isFetchingFreeThread: false });
      }
    }

    return true;
  },
};

export const mutations = {
  SET_THREAD(_state, thread) {
    if (!thread || !thread.id) {
      return;
    }
    const records = { ..._state.records };
    records[thread.id] = thread;
    _state.records = records;

    if (thread.thread_type === 'free') {
      _state.freeThread.data = thread;
    } else if (thread.thread_type === 'conversation') {
      _state.conversationThread.data = thread;
    }
  },

  ADD_THREAD(_state, thread) {
    _state.records[thread.id] = thread;

    if (thread.thread_type === 'free') {
      _state.freeThread.data = thread;
    } else if (thread.thread_type === 'conversation') {
      _state.conversationThread.data = thread;
    }
  },

  SET_CURRENT_THREAD_ID(_state, threadId) {
    // Fix: permettiamo null per resettare il puntatore
    _state.currentThreadId = threadId ?? null;
  },

  SET_FETCHING_MESSAGES_THREAD_ID(_state, threadId) {
    _state.fetchingMessagesThreadId = threadId ?? null;
  },

  SET_MESSAGES(_state, { messages, threadType }) {
    if (threadType === 'free') {
      _state.freeThread.messages = messages || [];
    } else if (threadType === 'conversation') {
      _state.conversationThread.messages = messages || [];
    }
  },

  ADD_MESSAGE(_state, { message, threadType }) {
    if (threadType === 'free') {
      if (!Array.isArray(_state.freeThread.messages)) {
        _state.freeThread.messages = [];
      }
      _state.freeThread.messages.push(message);
    } else if (threadType === 'conversation') {
      if (!Array.isArray(_state.conversationThread.messages)) {
        _state.conversationThread.messages = [];
      }
      _state.conversationThread.messages.push(message);

      // Aggiorniamo anche la cache
      const thread =
        _state.records[_state.currentThreadId] ||
        _state.conversationThread.data;
      if (thread?.conversation_id) {
        const cached = _state.conversationThreadCache[thread.conversation_id];
        if (cached) {
          cached.messages = [..._state.conversationThread.messages];

          _state.conversationThreadCache[thread.conversation_id] = cached;
        }
      }
    }
  },

  PREPEND_MESSAGES(_state, { messages, threadType }) {
    if (threadType === 'free') {
      if (!Array.isArray(_state.freeThread.messages)) {
        _state.freeThread.messages = [];
      }
      _state.freeThread.messages = [...messages, ..._state.freeThread.messages];
    } else if (threadType === 'conversation') {
      if (!Array.isArray(_state.conversationThread.messages)) {
        _state.conversationThread.messages = [];
      }
      _state.conversationThread.messages = [
        ...messages,
        ..._state.conversationThread.messages,
      ];
    }
  },

  SET_CONVERSATION_THREAD_CACHE(
    _state,
    { conversationId, data, messages, messagesMeta }
  ) {
    const cache = { ..._state.conversationThreadCache };
    cache[conversationId] = { data, messages: messages || [], messagesMeta };

    // LRU: limitiamo la cache a 20 entry
    const keys = Object.keys(cache);
    if (keys.length > 20) {
      const oldest = keys[0];
      delete cache[oldest];
    }

    _state.conversationThreadCache = cache;
  },

  CLEAR_CONVERSATION_THREAD_CACHE_ENTRY(_state, conversationId) {
    const cache = { ..._state.conversationThreadCache };
    delete cache[conversationId];
    _state.conversationThreadCache = cache;
  },

  CLEAR_FREE_THREAD(_state) {
    _state.freeThread.data = null;
    _state.freeThread.messages = [];
  },

  CLEAR_CONVERSATION_THREAD(_state) {
    _state.conversationThread.data = null;
    _state.conversationThread.messages = [];
  },

  SET_UI_FLAG(_state, flags) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...flags,
    };
  },

  SET_THREAD_MESSAGES(_state, messages) {
    const currentThread = _state.records[_state.currentThreadId];
    const threadType = currentThread?.thread_type || 'free';

    if (threadType === 'conversation') {
      _state.conversationThread.messages = messages;
    } else {
      _state.freeThread.messages = messages;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
